import { AgendaItemForm } from "@/components/agendaItems/AgendaItemForm";
import { AgendaModuleForm } from "@/components/agendaModules/AgendaModuleForm";
import { FormDialog } from "@/components/dialog/FormDialog";
import { DocumentForm } from "@/components/documents/DocumentForm";
import {
  AGENDA_ITEM_MOTIF,
  AGENDA_MODULE_MOTIF,
  DOCUMENT_MOTIF,
  MEETING_MOTIF,
} from "@/lib/constants";
import { MutationSystem } from "@/lib/queuedMutations";
import { cn } from "@repo/shared/utils";
import { DeltaUndoStack } from "@repo/lib/undoStack";
import type {
  AgendaItem,
  AgendaModule,
  Document,
  Meeting,
  Team,
  Tenant,
  User,
} from "@repo/types";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useDebounce } from "@repo/ui/components/Base/hooks/useDebounce";
import { useSticky } from "@repo/ui/components/Base/hooks/useSticky";
import {
  ChevronLeftIcon,
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  Redo2Icon,
  Undo2Icon,
  XIcon,
} from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { MeetingForm } from "@/components/meetings/MeetingForm";
import { type PromiseGuts, promiseGuts } from "@repo/lib/promiseGuts";
import {
  SearchableAgendaItemTable,
  SearchableAgendaModuleTable,
  SearchableDocumentsTable,
} from "@/components/common/SearchableTables";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { Button } from "@repo/ui/components/Base/Button";
import { useCopyAgendaModuleTemplate } from "@/hooks/useCopyAgendaTemplate";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { AgendaItemDefaults } from "@/components/agendaItems/AgendaItemDefaults";
import { CheckboxInput } from "@repo/ui/components/Base/CheckboxInput";
import {
  MeetingEditorContext,
  type MeetingEditorContextType,
  type UndoDelta,
} from "./MeetingEditorContext";
import type { Collapsible, EditorObject, EditorType } from "./utilities";
import { ToolButton } from "./iconButtons";
import { MeetingEditorMeeting } from "./MeetingEditorMeeting";
import { useAgendaModuleList } from "@/hooks/queryHooks/agendaModuleHooks";
import { useAgendaItemList } from "@/hooks/queryHooks/agendaItemHooks";
import { useDocumentList } from "@/hooks/queryHooks/documentHooks";
import { useActiveTeamId } from "@/hooks/useActiveTeam";
import { useAgendaTemplateList } from "@/hooks/queryHooks/agendaHooks";
import { agendaRepo } from "@/repositories/agendaRepo";
import type { AgendaItemDto } from "@/repositories/agendaItemRepo";
import type { AgendaModuleDto } from "@/repositories/agendaModuleRepo";
import type { DocumentDto } from "@/repositories/documentRepo";

// #region Editor Forms

const editorFormDetails: Record<
  EditorType,
  {
    editTitle: string;
    createTitle: string;
    loadTitle: string;
    headerClass: string;
  }
> = {
  meeting: {
    editTitle: "Edit Meeting Details",
    createTitle: "Create Meeting",
    loadTitle: "Select Meeting",
    headerClass: MEETING_MOTIF,
  },
  agendaModule: {
    editTitle: "Edit Agenda Section",
    createTitle: "Create Agenda Section",
    loadTitle: "Load Agenda Section Template",
    headerClass: AGENDA_MODULE_MOTIF,
  },
  agendaItem: {
    editTitle: "Edit Agenda Item",
    createTitle: "Create Agenda Item",
    loadTitle: "Add Agenda Item",
    headerClass: AGENDA_ITEM_MOTIF,
  },
  document: {
    editTitle: "Edit Document",
    createTitle: "Create Document",
    loadTitle: "Attach Document",
    headerClass: DOCUMENT_MOTIF,
  },
};

// #endregion

// #region Meeting Editor

export interface MeetingEditorProps {
  viewOnly?: boolean;
  tenant: Tenant;
  meeting: Meeting;
  team: Team;
  user: User;
  userMap: Record<string, User>;
  canViewConfidential?: boolean;
  canShowDetails?: boolean;
  onBack?: () => void;
}

export const MeetingEditor = ({
  viewOnly = false,
  tenant,
  meeting,
  team,
  user,
  userMap,
  canViewConfidential,
  canShowDetails,
  onBack,
}: MeetingEditorProps) => {
  const mutationSystem = useMemo(() => new MutationSystem(), []);
  const tenantId = tenant.id;

  // set up undo stack
  const [hasUndo, setHasUndo] = useState<UndoDelta | null>(null);
  const [hasRedo, setHasRedo] = useState<UndoDelta | null>(null);
  const undoStack = useMemo(() => {
    const undoStack = new DeltaUndoStack<UndoDelta>(
      (undoDelta) => undoDelta.undo(),
      (redoDelta) => redoDelta.redo(),
      20
    );
    undoStack.onStackChanged = () => {
      setHasUndo(undoStack.nextUndo());
      setHasRedo(undoStack.nextRedo());
    };
    return undoStack;
  }, []);

  // set up create / edit popup handler
  const [editorOpen, setEditorOpen] = useState(false);
  const [loaderOpen, setLoaderOpen] = useState(false);
  const [objectId, setObjectId] = useState("");
  const [objectType, setObjectType] = useState<EditorType | null>(null);
  const [editObject, setEditObject] = useState<EditorObject>();
  const [responsePromise, setResponsePromise] =
    useState<PromiseGuts<EditorObject | null> | null>(null);

  const formDetails = editorFormDetails[objectType ?? "agendaItem"];

  const openEditForm = async <T extends EditorObject>(
    type: EditorType,
    object: T | null | undefined
  ) => {
    const promise = promiseGuts<EditorObject | null>();
    setObjectType(type);
    setObjectId(object?.id ?? "new");
    setEditObject(object ?? undefined);
    setEditorOpen(true);
    setLoaderOpen(false);
    setResponsePromise(promise);
    return promise.promise as Promise<T | null>;
  };

  const openLoadForm = async <T extends EditorObject>(type: EditorType) => {
    const promise = promiseGuts<EditorObject | null>();
    setObjectType(type);
    setObjectId("");
    setEditObject(undefined);
    setLoaderOpen(true);
    setEditorOpen(false);
    setResponsePromise(promise);
    return promise.promise as Promise<T | null>;
  };

  const [loading, setLoading] = useState(false);
  const [additionalDetails, setAdditionalDetails] = useState(false);

  const activeTeamId = team.id;

  // prepare searchable tables, and copyable template sections
  const { data: filteredAgendaModules } = useAgendaModuleList(
    tenantId,
    activeTeamId
  );
  const { data: filteredAgendaItems } = useAgendaItemList(
    tenantId,
    activeTeamId
  );
  const { data: filteredDocuments } = useDocumentList(tenantId, activeTeamId);

  const copyAgendaModuleMutation = useCopyAgendaModuleTemplate(tenantId, false);

  // set up template selector
  const { data: agendaTemplates } = useAgendaTemplateList(
    tenantId,
    useActiveTeamId() || ""
  );

  const filteredAgendaTemplates = useMemo(
    () =>
      agendaTemplates?.filter((template) => template.teamId === activeTeamId) ??
      [],
    [agendaTemplates, activeTeamId]
  );

  const [selectedTemplate, setSelectedTemplate] = useState("none");
  const [disableUseTemplate, setDisableUseTemplate] = useState(false);

  const handleUseTemplate = useFutureCallback(async () => {
    const agendaId = meeting.agendaId;
    if (!agendaId) return;

    // find selected template
    const template = filteredAgendaTemplates.find(
      (template) => template.id === selectedTemplate
    );
    if (!template) return;

    if (!template.agendaModules?.length) {
      // If the template actually has no agendaModules, just reset the template selector.
      setSelectedTemplate("none");
      return;
    }

    // disable template selection while loading
    setDisableUseTemplate(true);

    // copy template sections
    const agendaModuleIds: string[] = [];
    for (const templateAgendaModule of template.agendaModules ?? []) {
      const agendaModule = await copyAgendaModuleMutation.copyAsync(
        templateAgendaModule,
        activeTeamId
      );

      if (agendaModule) agendaModuleIds.push(agendaModule.id);
    }

    // update sections list on agenda
    const updatedAgenda = await agendaRepo.update(tenantId, agendaId, {
      // add in the copied sections
      agendaModuleIds,
      // copy the letterhead from the template
      letterHeadId: template.letterHeadId,
    });
    if (updatedAgenda) {
      mutationSystem.submitNewer("agenda:" + agendaId, updatedAgenda);
    }

    // re-enable template selection (which should disappear)
    setDisableUseTemplate(false);

    // turn off preview mode
    setSelectedTemplate("none");
  });

  // set up 'collapse all' button
  const collapseSet = useRef(new Set<Collapsible>());

  const [allCollapsed, setAllCollapsed] = useState(false);
  const updateAllCollapsed = useDebounce(() => {
    let allCollapsed = true;
    for (const collapse of collapseSet.current) {
      if (
        collapse.type === "agendaModule" &&
        collapse.canCollapse() &&
        !collapse.isCollapsed()
      ) {
        allCollapsed = false;
        break;
      }
    }
    setAllCollapsed(allCollapsed);
  }, 1);

  // make floating buttons sticky
  const { stickyRef: stickyTools } = useSticky({
    yStick: "top",
    topStick: 30,
    top: 12,
    right: 12,
    bottom: 8,
  });
  const { stickyRef: stickyBack } = useSticky({
    yStick: "bottom",
    bottomStick: 6,
    top: 12,
    left: 12,
    bottom: 8,
  });

  // build context
  const context: MeetingEditorContextType = {
    viewOnly,
    mutationSystem,
    tenant,
    meeting,
    team,
    user,
    userMap,
    timeZone: tenant.timeZone ?? "America/New_York",
    openEditForm,
    openLoadForm,
    collapseSet: collapseSet.current,
    updateAllCollapsed,
    undoStack,
    previewTemplate:
      viewOnly || !selectedTemplate || selectedTemplate === "none"
        ? undefined
        : selectedTemplate,
    canViewConfidential: !!canViewConfidential,
    additionalDetails: !!canShowDetails && additionalDetails,
  };

  const agendaItemDefaults = { requestedApprovalDate: meeting.startTime };

  return (
    <MeetingEditorContext.Provider value={context}>
      <AgendaItemDefaults defaults={agendaItemDefaults} showPending={false}>
        <div className="p-16 pb-24 relative border border-gray-300 rounded-lg">
          {!!filteredAgendaTemplates.length &&
          !meeting.agenda?.agendaModuleIds.length &&
          !viewOnly ? (
            <div className="absolute left-2 top-2 flex flex-row gap-2">
              <SelectInput
                containerStyle={/*@tw*/ "min-w-48 m-0"}
                value={selectedTemplate}
                disabled={disableUseTemplate}
                onValueChange={(value) => setSelectedTemplate(value)}
                options={[
                  <SelectItem key="none" value="none">
                    &lt;None&gt;
                  </SelectItem>,
                  ...filteredAgendaTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      {template.name}
                    </SelectItem>
                  )),
                ]}
              />

              <Button
                disabled={
                  disableUseTemplate ||
                  !selectedTemplate ||
                  selectedTemplate === "none"
                }
                onClick={handleUseTemplate}
              >
                Use Template
              </Button>
            </div>
          ) : (
            canShowDetails && (
              <div className="absolute left-6 top-5">
                <CheckboxInput
                  orientation="horizontal"
                  label="Additional Details"
                  checked={additionalDetails}
                  onCheckedChange={(checked) => setAdditionalDetails(!!checked)}
                />
              </div>
            )
          )}

          <div className="z-10" ref={stickyTools}>
            <ToolButton
              title={allCollapsed ? "Expand All" : "Collapse All"}
              onClick={() => {
                for (const collapse of collapseSet.current) {
                  collapse.setCollapsed(!allCollapsed);
                }
              }}
            >
              {allCollapsed ? (
                <ChevronsUpDownIcon className="w-6 text-gray-500" />
              ) : (
                <ChevronsDownUpIcon className="w-6 text-gray-500" />
              )}
            </ToolButton>
            {!viewOnly && (
              <>
                <ToolButton
                  title={"Undo" + (hasUndo ? " " + hasUndo.label : "")}
                  onClick={() => undoStack.undo()}
                >
                  <Undo2Icon
                    className={cn("w-6 text-gray-500", hasUndo || "opacity-20")}
                  />
                </ToolButton>
                <ToolButton
                  title={"Redo" + (hasRedo ? " " + hasRedo.label : "")}
                  onClick={() => undoStack.redo()}
                >
                  <Redo2Icon
                    className={cn("w-6 text-gray-500", hasRedo || "opacity-20")}
                  />
                </ToolButton>
              </>
            )}
          </div>

          <div className="z-10" ref={stickyBack}>
            <ToolButton
              title="Back"
              className={cn(
                "text-white w-auto pl-2 pr-3",
                viewOnly ? "bg-destructive" : "bg-primary"
              )}
              onClick={onBack}
            >
              {viewOnly ? (
                <>
                  <ChevronLeftIcon className="w-6 text-white mr-1" />
                  Back
                </>
              ) : (
                <>
                  <XIcon className="w-6 text-white mr-1" />
                  Close Editing
                </>
              )}
            </ToolButton>
          </div>

          <MeetingEditorMeeting />

          <FormDialog
            key={objectType + ":" + objectId}
            open={editorOpen}
            setOpen={setEditorOpen}
            title={editObject ? formDetails.editTitle : formDetails.createTitle}
            preventDefault
            showTrigger={false}
            headerClassName={formDetails.headerClass}
            usesWithinDialog
            form={({ handleCancel = () => {}, handleComplete = () => {} }) => {
              const props = {
                tenantId: tenant.id,
                handleCancel: () => {
                  handleCancel();
                  responsePromise?.resolve(null);
                },
                handleComplete,
                onSubmit: (
                  object: Meeting | AgendaModule | AgendaItem | Document
                ) => {
                  const mutationKey = objectType + ":" + object.id;
                  mutationSystem.submitNewer(mutationKey, object);
                  responsePromise?.resolve(object);
                },
                withinDialog: true,
              };

              switch (objectType) {
                case "meeting":
                  return (
                    <MeetingForm
                      hidePreview
                      meetingFormData={editObject as Meeting | undefined}
                      {...props}
                    />
                  );
                case "agendaModule":
                  return (
                    <AgendaModuleForm
                      agendaModuleFormData={
                        editObject as AgendaModuleDto | undefined
                      }
                      isTemplate={false}
                      {...props}
                    />
                  );
                case "agendaItem":
                  return (
                    <AgendaItemForm
                      agendaItemFormData={
                        editObject as AgendaItemDto | undefined
                      }
                      {...props}
                    />
                  );
                case "document":
                  return (
                    <DocumentForm
                      documentFormData={editObject as DocumentDto | undefined}
                      {...props}
                    />
                  );
              }
            }}
          />

          <FormDialog
            key={objectType + ":load"}
            open={loaderOpen}
            setOpen={setLoaderOpen}
            title={formDetails.loadTitle}
            preventDefault
            showTrigger={false}
            headerClassName={formDetails.headerClass}
            usesWithinDialog
            form={({ handleCancel = () => {}, handleComplete = () => {} }) => {
              let table: React.ReactNode = null;

              const onAdd = async (item: EditorObject | null) => {
                if (
                  objectType === "agendaModule" &&
                  (item as AgendaModule).template
                ) {
                  setLoading(true);
                  item =
                    (await copyAgendaModuleMutation.copyAsync(
                      item as AgendaModule,
                      activeTeamId
                    )) ?? null;
                  setLoading(false);
                }
                responsePromise?.resolve(item);
                handleComplete();
              };

              switch (objectType) {
                case "agendaModule":
                  table = (
                    <SearchableAgendaModuleTable
                      data={filteredAgendaModules ?? []}
                      showCreate={false}
                      disabled={loading}
                      onAdd={onAdd}
                    />
                  );
                  break;
                case "agendaItem":
                  table = (
                    <SearchableAgendaItemTable
                      data={filteredAgendaItems ?? []}
                      showCreate={false}
                      disabled={loading}
                      onAdd={onAdd}
                    />
                  );
                  break;
                case "document":
                  table = (
                    <SearchableDocumentsTable
                      data={filteredDocuments ?? []}
                      showCreate={false}
                      disabled={loading}
                      onAdd={onAdd}
                    />
                  );
                  break;
              }

              return (
                <>
                  <DialogBody>{table}</DialogBody>
                  <DialogFooter className="justify-between items-center">
                    <Button
                      variant="destructive"
                      disabled={loading}
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                    <p>Pick a row to submit it.</p>
                  </DialogFooter>
                </>
              );
            }}
          />
        </div>
      </AgendaItemDefaults>
    </MeetingEditorContext.Provider>
  );
};

// #endregion
